import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { translateBackend } from 'assets/i18n/config';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { usePageState } from '../../../util/usePageState';
import ModelPortfolioAssetAllocation from './components/modelPortfolioAssetAllocation';
import ModelPortfolioGeneralDetails from './components/modelPortfolioGeneralDetails';
import ModelPortfolioAuditLogs from './components/modelPortfolioAuditLogs';
import ModelPortfolioPolicyAndAssets from './components/modelPortfolioPolicyAndAssets';
import {
  Grid, Skeleton, Typography,
} from '../../1-primative';
import { Menu, MenuItem } from '../../2-component';
import { SecondaryTabs } from '../../3-pattern';
import { FETCH_MODEL_PORTFOLIO } from '../../4-module/workflowCompletion/subSteps/queries';
import { ModelPortfolioData } from '../../4-module/workflowCompletion/subSteps';
import { IconButton } from '../../2-component/iconButton/iconButton';
import { TRANSITION_MODEL } from '../../../pages/buildModels/components/editDetails';
import { Badge, getStateBadgeColor } from '../..';
import { usePermissions } from '../../../providers/userContextProvider';
import { CloneModelPortfolioDialog } from './components/cloneModelPortfolioDialog';

const ModelPortfolioDetails = () => {
  const [model, setModel] = useState<ModelPortfolioData>({} as ModelPortfolioData);
  const [unsavedChanges, setUnsavedChanges] = useState<ModelPortfolioData | null>(null);
  const { permissions } = usePermissions();
  const { t } = useTranslation(['modelPortfolios']);
  const params = useParams();
  const modelPortfolioId = params.id;

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);

  const portfolioState = (status: string) => status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

  const [transitionModelPortfolio] = useMutation(TRANSITION_MODEL, {
    refetchQueries: ['fetchModelPortfolio'],
  });

  const { loading } = useQuery(FETCH_MODEL_PORTFOLIO, {
    variables: {
      input: {
        modelPortfolioId,
      },
    },
    onCompleted: (result) => {
      setModel(result.fetchModelPortfolio.modelPortfolio);
      setUnsavedChanges(null);
    },
  });

  const handleModelUpdate = (updatedModel: ModelPortfolioData) => {
    setUnsavedChanges(updatedModel);
  };

  const handleModelSave = (savedModel: ModelPortfolioData) => {
    setModel(savedModel);
    setUnsavedChanges(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
  };

  const transition = async (action: 'activate' | 'draft' | 'archive') => {
    transitionModelPortfolio({
      variables: {
        input: {
          modelPortfolioId,
          transition: action,
        },
      },
    });
  };

  /* Added permissions to the tabs so we can handle different
  permissions for each tab in the future.
  */
  const availableTabs = [
    {
      id: 'portfolio-asset-allocation',
      label: t('portfolio.tabs.assetAllocation'),
      permission: 'read:model_portfolios',
    },
    {
      id: 'portfolio-details',
      label: t('portfolio.tabs.details'),
      permission: 'read:model_portfolios',
    },
    {
      id: 'portfolio-policy',
      label: t('portfolio.tabs.invPolicyStatement'),
      permission: 'read:model_portfolios',
    },
    {
      id: 'portfolio-audit',
      label: t('portfolio.tabs.auditLog'),
      permission: 'read:model_portfolios',
    },
  ].filter((availableTab) => permissions.includes(availableTab.permission));

  const [tab, setTab] = usePageState(availableTabs[0]?.id || 'portfolio-asset-allocation', 'tab');

  if (availableTabs.length === 0) return null;
  return loading ? <Skeleton variant='rectangular' height={100} /> : (
    <>
      <Grid
        item
        xs={12}
        md={8}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography
          data-testid="model-portfolio-details-title"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          variant='displayLargeVariant'
        >
          {translateBackend(model?.translatedName)}
        </Typography>
        <Badge
          testId="model-portfolio-details-state-badge"
          label={portfolioState(model?.state || '')}
          color={getStateBadgeColor(model?.state)}
        />
        <IconButton
          data-testid="model-portfolio-details-actions-menu-button"
          onClick={handleMenuClick}
          size="medium"
          sx={{ ml: 'auto' }}
        >
          <MoreVertIcon />
        </IconButton>
      </Grid>
      <SecondaryTabs
        data-testid="model-portfolio-details-tabs"
        tabs={availableTabs.map((tabItem) => ({
          label: tabItem.label,
          icon: '',
          key: tabItem.id,
        }))}
        activeTab={tab} setActiveTab={(ta) => {
          setTab(ta.toString());
        }} />
      {tab === 'portfolio-asset-allocation' && <ModelPortfolioAssetAllocation model={model} setModel={handleModelUpdate} loading={loading} />}
      {tab === 'portfolio-details' && (
        <ModelPortfolioGeneralDetails
          model={unsavedChanges || model}
          loading={loading}
          setModel={handleModelUpdate}
          onSave={handleModelSave}
        />
      )}
      {tab === 'portfolio-policy' && <ModelPortfolioPolicyAndAssets model={model} setModel={handleModelUpdate} loading={loading} />}
      {tab === 'portfolio-audit' && <ModelPortfolioAuditLogs model={model} loading={loading} />}

      {/* Actions Menu */}
      <Menu
        data-testid="model-portfolio-details-actions-menu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.stopPropagation();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {model?.assignable && (
          <MenuItem
            data-testid="model-portfolio-details-actions-menu-clone-button"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              setCloneDialogOpen(true);
              handleMenuClose(e);
            }}>
            {t('modelPortfolioTable.actions.clone')}
          </MenuItem>
        )}
        {model?.state !== 'ACTIVE' && (
          <MenuItem
            data-testid="model-portfolio-details-actions-menu-activate-button"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              transition('activate');
              handleMenuClose(e);
            }}>
            {t('modelPortfolioTable.actions.activate')}
          </MenuItem>
        )}
        {model?.state !== 'DRAFT' && (
          <MenuItem
            data-testid="model-portfolio-details-actions-menu-draft-button"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              transition('draft');
              handleMenuClose(e);
            }}>
            {t('modelPortfolioTable.actions.draft')}
          </MenuItem>
        )}
        {model?.state !== 'ARCHIVED' && (
          <MenuItem
            data-testid="model-portfolio-details-actions-menu-archive-button"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              transition('archive');
              handleMenuClose(e);
            }}>
            {t('modelPortfolioTable.actions.archive')}
          </MenuItem>
        )}
      </Menu>

      <CloneModelPortfolioDialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        modelId={modelPortfolioId ?? ''}
        modelName={model?.translatedName ?? { en: '', fr: '' }}
      />
    </>
  );
};

export default ModelPortfolioDetails;
